import {
  createBrandScale,
  createTheme,
  isExternalLink,
  ThemeProvider as TournatedThemeProvider,
} from "@tournated-com/ui";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { BrandFavicon } from "@/components/common";
import { MaintenanceNotice } from "@/components/maintenance";
import SplashScreen from "@/components/SplashScreen";
import { useBrandColorContext, useDateFormatContext } from "@/contexts";
import { BrandColorContextProvider, DEFAULT_PRIMARY_COLOR } from "@/contexts/BrandColorContext";
import { DateFormatContextProvider } from "@/contexts/DateFormatContext/DateFormatContext.provider";
import { DomainBrandingProvider } from "@/contexts/DomainBrandingContext/DomainBrandingContext.provider";
import { useLanguage } from "@/i18n";

export const CustomThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <BrandColorContextProvider>
      <DomainBrandingProvider>
        <BrandFavicon />
        <DateFormatContextProvider>
          <ThemeProviderInternal>{children}</ThemeProviderInternal>
        </DateFormatContextProvider>
      </DomainBrandingProvider>
    </BrandColorContextProvider>
  );
};

const createBrandTheme = (primaryColor: string) =>
  createTheme({
    light: {
      palette: {
        brand: createBrandScale(primaryColor, "light"),
      },
    },
    dark: {
      palette: {
        brand: createBrandScale(primaryColor, "dark"),
      },
    },
  });

const useThemeNavigate = () => {
  const navigate = useNavigate();

  return useCallback(
    (path: string) => {
      if (isExternalLink(path)) {
        window.open(path, "_blank");
        return;
      }

      navigate(path);
    },
    [navigate]
  );
};

const ThemeProviderInternal = ({ children }: { children: React.ReactNode }) => {
  const customNavigate = useThemeNavigate();
  const [language] = useLanguage();
  const { brandColor, isBrandingLoading } = useBrandColorContext();
  const primaryColor = brandColor || DEFAULT_PRIMARY_COLOR;
  const { dateFormat, timeFormat, timeZone } = useDateFormatContext();

  const theme = useMemo(() => createBrandTheme(primaryColor), [primaryColor]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty("--color-primary", primaryColor);
  }, [primaryColor]);

  return (
    <TournatedThemeProvider
      theme={theme}
      locale={language}
      dateFormat={dateFormat}
      timeFormat={timeFormat}
      timeZone={timeZone}
      navigate={customNavigate}
    >
      {isBrandingLoading ? <SplashScreen /> : children}
      {/* Outside the branding gate: the notice must show even when the backend is down. */}
      <MaintenanceNotice />
    </TournatedThemeProvider>
  );
};

/**
 * Default-branded theme with no backend-backed context, for screens rendered
 * while {@link CustomThemeProvider} is not mounted (the root error boundary).
 * Never nest it inside `CustomThemeProvider`: each theme provider injects global
 * CSS variables, so the inner one would override the platform's brand colours.
 */
export const FallbackThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const customNavigate = useThemeNavigate();
  const [language] = useLanguage();
  const theme = useMemo(() => createBrandTheme(DEFAULT_PRIMARY_COLOR), []);

  return (
    <TournatedThemeProvider theme={theme} locale={language} navigate={customNavigate}>
      {children}
    </TournatedThemeProvider>
  );
};
